import React, { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useBlocker } from 'react-router-dom'

import { ClickNavigation, ClickNavigationSection } from '../../../../components/NavigationWithSideBar/ClickNavigation'
import { getIntegrationSettingsSchema, IntegrationSettings } from './schema'
import { FormSaveBar } from '../../../../components/FormSaveBar/FormSaveBar'
import { useGetIntegrationAdminSettings } from './hooks'
import { IntegrationServiceId } from '../../../../common/integrations'
import { DebugZodErrors } from './ZodValidator'
import { defaultCRMAccountIntegrationConfig } from './AdminSettingsTabs/model'
import { useZocksQuota } from '../../../../components/ScopedBlock/hooks'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { Button } from '../../../../components/Button/Button'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { addToast } from '../../../../store/toast/toastSlice'

interface IntegrationAdminSettingsContainerProps {
  sections: ClickNavigationSection[]
  serviceId: IntegrationServiceId
}

export const IntegrationAdminSettingsContainer: React.FC<IntegrationAdminSettingsContainerProps> = ({ sections, serviceId }) => {
  const { isAccountUpdating, saveAccountMappingConfig, currentIntegrationAccountConfig } = useGetIntegrationAdminSettings(serviceId)
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()

  const [formKey, setFormKey] = useState(0)

  const { getQuotaValue } = useZocksQuota()

  const additionalObjectsLimit = getQuotaValue('integration.additionalObjMax.value')
  const integrationSettingsSchema = getIntegrationSettingsSchema(additionalObjectsLimit as number)
  const methods = useForm<IntegrationSettings>({
    resolver: zodResolver(integrationSettingsSchema),
    mode: 'onChange',
    defaultValues: currentIntegrationAccountConfig || defaultCRMAccountIntegrationConfig(serviceId),
    shouldUnregister: false,
  })

  const {
    reset,
    formState: { isDirty, isSubmitting, isValid, errors },
  } = methods

  useEffect(() => {
    if (currentIntegrationAccountConfig) {
      reset(currentIntegrationAccountConfig)
    }
  }, [currentIntegrationAccountConfig, reset])

  const onSubmit = async (data: IntegrationSettings) => {
    await saveAccountMappingConfig(data)
    reset(data)
  }

  const handleDiscard = () => {
    reset()
    setFormKey((prevKey) => prevKey + 1)
  }

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty && !isSubmitting) {
        e.preventDefault()
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty, isSubmitting])

  const blocker = useBlocker(({ currentLocation, nextLocation }) => isDirty && !isSubmitting && currentLocation.pathname !== nextLocation.pathname)

  const handleExportAdminSettings = () => {
    if (currentIntegrationAccountConfig) {
      const blob = new Blob([JSON.stringify(currentIntegrationAccountConfig)], { type: 'application/json' })

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${accountId}_${serviceId}_settings.json`
      document.body.appendChild(link)
      link.click()

      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } else {
      dispatch(
        addToast({
          variant: 'info',
          title: 'Exporting to file',
          messages: ['Unable to export: no configuration has been saved. Please save your configuration first.'],
        }),
      )
    }
  }

  return (
    <FormProvider {...methods}>
      <form id='integration-settings-form' onSubmit={methods.handleSubmit(onSubmit)} className='d-flex flex-column min-vh-100'>
        <div className='flex-grow-1 zocks-pb-10.0'>
          <ClickNavigation
            sections={sections}
            defaultActiveSectionId={sections[0].id}
            key={formKey}
            NavigationFooter={<AdminSettingsNavigationFooter handleExportAdminSettings={handleExportAdminSettings} />}
          />
        </div>
        <FormSaveBar
          formId='integration-settings-form'
          isVisible={isDirty}
          isSubmitting={isSubmitting || isAccountUpdating}
          isValid={isValid}
          isDirty={isDirty}
          errors={errors}
          onDiscard={handleDiscard}
          showNavigationWarning={blocker.state === 'blocked'}
          onCancelLeave={() => blocker.reset?.()}
          onConfirmLeave={() => blocker.proceed?.()}
        />
        <DebugZodErrors shouldDebug={false} />
      </form>
    </FormProvider>
  )
}
interface AdminSettingsNavigationFooterProps {
  handleExportAdminSettings: () => void
}

const AdminSettingsNavigationFooter: React.FC<AdminSettingsNavigationFooterProps> = ({ handleExportAdminSettings }) => {
  return (
    <div>
      <ScopedBlock
        missingScopeAction={MissingScopeAction.DISABLE}
        scope={{
          scope: 'account:read',
          superScope: ['account:admin'],
        }}
      >
        <Button className='ms-1 button-link-small' onClick={handleExportAdminSettings}>
          Export settings to file
        </Button>
      </ScopedBlock>
    </div>
  )
}
